import { shortUuid } from '@tunasong/models'
import type { GearRequirement, ProfileGear, StageInput } from '@tunasong/schemas'

/**
 * Pure edits to a member's stage setup. Every edit stamps `updatedAt`, which drives "Last updated" and the rider
 * drift signal. A removal returns an `undo` that re-inserts the item into whatever the setup is when Undo is pressed,
 * so edits made in between are kept.
 */

export type NewStageInput = Omit<StageInput, 'id'>

export interface Removal<T> {
  gear: ProfileGear
  removed?: T
  undo(current: ProfileGear | undefined): ProfileGear
}

export const withUpdate = (
  gear: ProfileGear | undefined,
  patch: Partial<ProfileGear>,
  now = new Date()
): ProfileGear => ({
  requirements: [],
  ...gear,
  ...patch,
  updatedAt: now.toISOString(),
})

const insertAt = <T>(list: readonly T[], index: number, item: T) => [
  ...list.slice(0, Math.min(index, list.length)),
  item,
  ...list.slice(Math.min(index, list.length)),
]

export const addInputs = (
  gear: ProfileGear | undefined,
  inputs: NewStageInput[],
  newId: () => string = shortUuid,
  now = new Date()
): ProfileGear =>
  withUpdate(gear, { inputs: [...(gear?.inputs ?? []), ...inputs.map(input => ({ ...input, id: newId() }))] }, now)

export const updateInput = (
  gear: ProfileGear | undefined,
  id: string,
  patch: Partial<NewStageInput>,
  now = new Date()
): ProfileGear =>
  withUpdate(
    gear,
    { inputs: (gear?.inputs ?? []).map(input => (input.id === id ? { ...input, ...patch } : input)) },
    now
  )

export const removeInput = (gear: ProfileGear | undefined, id: string, now = new Date()): Removal<StageInput> => {
  const inputs = gear?.inputs ?? []
  const index = inputs.findIndex(input => input.id === id)
  const removed = index === -1 ? undefined : inputs[index]
  return {
    gear: withUpdate(gear, { inputs: inputs.filter(input => input.id !== id) }, now),
    removed,
    undo: current =>
      removed
        ? withUpdate(current, { inputs: insertAt(current?.inputs ?? [], index, removed) })
        : withUpdate(current, {}),
  }
}

export const addRequirement = (gear: ProfileGear | undefined, item: GearRequirement, now = new Date()): ProfileGear =>
  withUpdate(gear, { requirements: [...(gear?.requirements ?? []), item] }, now)

export const updateRequirement = (
  gear: ProfileGear | undefined,
  index: number,
  patch: Partial<GearRequirement>,
  now = new Date()
): ProfileGear =>
  withUpdate(
    gear,
    { requirements: (gear?.requirements ?? []).map((item, i) => (i === index ? { ...item, ...patch } : item)) },
    now
  )

export const removeRequirement = (
  gear: ProfileGear | undefined,
  index: number,
  now = new Date()
): Removal<GearRequirement> => {
  const requirements = gear?.requirements ?? []
  const removed = requirements[index]
  return {
    gear: withUpdate(gear, { requirements: requirements.filter((_, i) => i !== index) }, now),
    removed,
    undo: current =>
      removed
        ? withUpdate(current, { requirements: insertAt(current?.requirements ?? [], index, removed) })
        : withUpdate(current, {}),
  }
}
